import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject, Subscription } from 'rxjs';
import { FileManagerTypeEnum, IGetFilesByFolderRequestDetails } from '../services/file-manager-api';
import { catchError, finalize, first, map, share, tap } from 'rxjs/operators';
import { FilesListApiService } from '../../typescript-angular-client/api/fileList/filesList.service';
import { FileManagerResults } from '../../typescript-angular-client/api/fileList/filesList.service.model';
import {
  DeleteEntityModel,
  FileModel,
  FolderTreeNode,
  MoveFilesModel
} from '../../typescript-angular-client/model/models';
import { SelectedEntities } from '../models/selected-entities.model';
import { IFilesListService, IFolderInfo } from './files-list.service.model';
import { OrderByEnum, OrderDirectionEnum } from '../../file-manager/services/file-manager-api';

export const FOLDER_KEY_FOR_FILESONLYMODE = '-1';

@Injectable({ providedIn: 'root' })
export class NewFilesListService implements IFilesListService {
  public folderInfo: IFolderInfo = {};
  private fileSubject = new BehaviorSubject<FileModel[]>([]);
  private totalCount = new BehaviorSubject(0);
  private loadingSubject = new BehaviorSubject(false);
  private isFilesOnlyModeSubject = new BehaviorSubject(false);
  private subcriptions = new Subscription();
  private loadByfolderSubscription = new Subscription();
  private deletedFiles = new BehaviorSubject(false);
  private pageLoadingRequest: IGetFilesByFolderRequestDetails = {
    Count: 16,
    OrderBy: OrderByEnum.Name,
    OrderByDirection: OrderDirectionEnum.ASC,
    FileManagerType: 1,
    isCountChanged: false,
    searchCondition: '',
    Page: 0,
    folderId: '',
    SearchAllFolders: false,
    ExcludeFolders: false
  };

  get deletedFiles$(): Observable<boolean> {
    return this.deletedFiles.asObservable().pipe(share());
  }

  get files$(): Observable<FileModel[]> {
    return this.fileSubject.asObservable().pipe(share());
  }

  get isLoading$(): Observable<boolean> {
    return this.loadingSubject.asObservable().pipe(share());
  }

  get isFilesOnlyMode$(): Observable<boolean> {
    return this.isFilesOnlyModeSubject.asObservable().pipe(share());
  }

  get isFilesOnlyMode(): boolean {
    return this.isFilesOnlyModeSubject.getValue();
  }

  constructor(private fileListApiService: FilesListApiService, private zone: NgZone) {}

  public setFilesOnlyMode = (isFileOnlyMode: boolean, currentFolderNode: FolderTreeNode, dialogType: FileModel.FileTypeEnum, pageSize: number) => {
    this.isFilesOnlyModeSubject.next(isFileOnlyMode);
    this.loadByFolder(currentFolderNode, dialogType, pageSize);
  }

  public loadByPage = (currentFolderKey: string, page: number, dialogType: FileModel.FileTypeEnum, pageSize: number, searchTerm: string = ''): Observable<FileManagerResults> => {
    if (!currentFolderKey || page < 0) {
      return of(<FileManagerResults>{ Files: [] });
    }
    if (dialogType === FileModel.FileTypeEnum.IMAGES) {
      this.pageLoadingRequest.FileManagerType = FileManagerTypeEnum.IMAGES;
    }
    if (dialogType === FileModel.FileTypeEnum.DOCUMENTS) {
      this.pageLoadingRequest.FileManagerType = FileManagerTypeEnum.DOCUMENTS;
    }
    this.pageLoadingRequest.SearchAllFolders = this.isFilesOnlyModeSubject.getValue();
    this.pageLoadingRequest.Count = pageSize;

    const data = Object.assign({}, this.pageLoadingRequest) as IGetFilesByFolderRequestDetails;
    data.folderId = currentFolderKey;
    data.Page = page;
    data.searchCondition = searchTerm;
    data.ExcludeFolders = true;

    return this.fileListApiService.getFiles(data)
      .pipe(tap((r) => this.folderInfo[currentFolderKey] = r.TotalCount));
  }

  public loadByFolder = (folder: FolderTreeNode, dialogType: FileModel.FileTypeEnum, pageSize: number): void => {
    const isFilesOnlyMode = this.isFilesOnlyModeSubject.getValue();
    if (!folder && !isFilesOnlyMode) {
      return;
    }
    if (dialogType === FileModel.FileTypeEnum.IMAGES) {
      this.pageLoadingRequest.FileManagerType = FileManagerTypeEnum.IMAGES;
    }
    if (dialogType === FileModel.FileTypeEnum.DOCUMENTS) {
      this.pageLoadingRequest.FileManagerType = FileManagerTypeEnum.DOCUMENTS;
    }
    this.pageLoadingRequest.SearchAllFolders = isFilesOnlyMode;
    this.pageLoadingRequest.Count = pageSize;

    if (this.loadByfolderSubscription) {
      this.loadByfolderSubscription.unsubscribe();
    }
    this.fileSubject.next([]);
    this.loadingSubject.next(true);
    const data = Object.assign({}, this.pageLoadingRequest) as IGetFilesByFolderRequestDetails;
    data.folderId = isFilesOnlyMode ? null : folder.key;
    data.Page = 1;
    data.ExcludeFolders = isFilesOnlyMode;

    this.loadByfolderSubscription = this.fileListApiService.getFiles(data)
      .pipe(catchError(() => of(<FileManagerResults>{ Files: [], TotalCount: 0 })))
      .subscribe((res) => {
        this.zone.run(() => {
          const folderKey = isFilesOnlyMode ? FOLDER_KEY_FOR_FILESONLYMODE : folder.key;
          this.folderInfo[folderKey] = res.TotalCount;
          this.fileSubject.next(res.Files);
          this.totalCount.next(res.TotalCount);
          this.loadingSubject.next(false);
        });
      });
  }

  public deleteFiles = (files: FileModel[]): Observable<string[]> => {
    if (!files || files.length <= 0) {
      return;
    }
    const response = this.fileListApiService.deleteEntities(files.map(file => <DeleteEntityModel>{
      EntityId: file.Id,
      IsFolder: file.IsFolder
    }));
    response.pipe(first()).subscribe(() => this.deletedFiles.next(true));
    return response;
  }

  public updateFile = (file: FileModel, dialogType: FileModel.FileTypeEnum): Observable<void> => {
    if (!file) {
      return;
    }
    return this.fileListApiService.updateFile({
      Id: file.Id,
      Name: file.Name,
      ParentFolderId: file.ParentFolderId
    }, dialogType);
  }

  public search = (searchTerm: string, folder: FolderTreeNode, dialogType: FileModel.FileTypeEnum, pageSize: number): void => {
    if (!searchTerm || searchTerm === '') {
      this.loadByFolder(folder, dialogType, pageSize);
      return;
    }

    this.pageLoadingRequest.Count = pageSize;

    this.fileSubject.next([]);
    this.loadingSubject.next(true);

    this.fileListApiService.getFiles({
      Count: pageSize,
      OrderBy: OrderByEnum.Name,
      OrderByDirection: OrderDirectionEnum.ASC,
      FileManagerType: dialogType as any,
      folderId: folder.key,
      isCountChanged: false,
      searchCondition: searchTerm,
      Page: 1,
      SearchAllFolders: true,
      ExcludeFolders: this.isFilesOnlyModeSubject.getValue()
    }).subscribe((res) => {
      this.zone.run(() => {
        this.fileSubject.next(res.Files);
        this.totalCount.next(res.TotalCount);
        this.loadingSubject.next(false);
      });
    });
  }

  public moveFiles = (dest: FileModel, source: FileModel[]): Observable<void> => {
    if (!dest || !source || source.length <= 0) {
      return;
    }
    return this.fileListApiService.moveFiles(<MoveFilesModel>{
      parentFolderId: dest.Id,
      fileIds: source.map(x => x.Id)
    });
  }

  public clear = (): void => {
    this.subcriptions.unsubscribe();
    this.fileSubject.next(null);
  }
}

@Injectable()
export class FilesListService {
  public files: Array<FileModel>;
  public totalCount: number;
  public isLoading = true;
  public selectedEntities = new SelectedEntities();
  public selectedFolder: string;
  public nextPage = 0;
  public searchTerm = '';
  private onFileSelected$ = new BehaviorSubject<FileModel>(null);
  private onSeveralFilesSelected$ = new Subject<boolean>();
  public isDirty = false;
  private currentFileManagerType: FileManagerTypeEnum = null;

  constructor(private fileListApiService: FilesListApiService) {}

  get onFileSelected(): Observable<FileModel> {
    return this.onFileSelected$.asObservable();
  }

  get onSeveralFilesSelected(): Observable<boolean> {
    return this.onSeveralFilesSelected$.asObservable();
  }

  private getParams(): IGetFilesByFolderRequestDetails {
    return {
      Count: 16,
      OrderBy: OrderByEnum.Name,
      OrderByDirection: OrderDirectionEnum.ASC,
      Page: this.nextPage,
      FileManagerType: this.currentFileManagerType,
      searchCondition: this.searchTerm,
      folderId: this.selectedFolder || '0',
      isCountChanged: false,
      SearchAllFolders: false,
      ExcludeFolders: false
    };
  }

  private formatDate(d: Date): string {
    const mS = ['Jan', 'Feb', 'March', 'April', 'May', 'June', 'July', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
    const month = mS[d.getMonth()];
    const day = d.getDate();
    const year = d.getFullYear();
    return month + ' ' + day + ', ' + year;
  }

  private setSelectedFile = (file: FileModel): void => {
    this.selectedEntities.AddSelectedFile(file);
    if (this.selectedEntities.selectedFiles.length === 1) {
      this.onFileSelected$.next(this.selectedEntities.selectedFiles[0]);
    }
    if (this.selectedEntities.selectedFiles.length === 0) {
      this.onFileSelected$.next(null);
    }
    this.onSeveralFilesSelected$.next(this.selectedEntities.selectedFiles.length > 1);
  }

  public getDate = (date: string): string => {
    const parsedDate = new Date(date);
    const jsDate = new Date(parsedDate);
    return this.formatDate(jsDate);
  }

  public refresh = (isSelectFirstImage = false, pageSize: number): void => {
    this.onSeveralFilesSelected$.next(false);
    this.onFileSelected$.next(null);
    this.selectedEntities = new SelectedEntities();
    this.nextPage = 0;
    if (this.files.length > 0) {
      this.files.splice(0, this.files.length);
    }
    this.getFilesList(isSelectFirstImage, pageSize);
  }

  private getFilesList = (isSelectFirstImage = false, pageSize: number): Observable<FileModel[]> => {
    const params = this.getParams();
    params.Count = pageSize;
    this.isLoading = true;
    return this.fileListApiService.getFiles(params)
      .pipe(tap((filesListData: FileManagerResults) => {
          const files = filesListData.Files;
          if (filesListData && files) { // Success
            if (files.length > 0 && isSelectFirstImage === true) {
              this.setSelectedFile(null);
              this.setSelectedFile(files[0]);
            }
            if (typeof this.totalCount === 'undefined') {
              this.totalCount = filesListData.TotalCount;
            }
            if (typeof this.files === 'undefined') {
              this.files = files;
            } else {
              this.files.push.apply(this.files, files);
            }
          }
        }),
        map(x => x.Files),
        finalize(() => this.isLoading = false));
  }
}
